import { useCallback, useEffect, useReducer, useRef } from "react";
import { Duration } from "luxon";

import {
  acceptInviteByToken,
  fetchInviteByToken,
  InviteSummary,
} from "@/pages/invites/invite.utils";
import { InvitePageViewKind } from "@/pages/invites/invite.types";
import { sleep } from "@/domains/event-loop/synchronization";
import {
  InviteStatusType,
  getInviteAcceptedMessage,
  getInviteStatusCopy,
  getNormalizedInviteToken,
} from "@/pages/invites/invites-page.utils";

const MIN_LOADING_DURATION = Duration.fromObject({ seconds: 2 });

type InvitePageState = {
  kind: InvitePageViewKind;
  invite: InviteSummary | null;
  hasAccepted: boolean;
  isAccepting: boolean;
  acceptError: string | null;
  statusTitle: string | null;
  statusBody: string | null;
  successMessage: string | null;
  lastKnownSharedItemKind?: InviteSummary["sharedItemKind"];
};

type InvitePageAction =
  | { type: "reset"; payload: InvitePageState }
  | { type: "start-loading" }
  | { type: "set-invite"; invite: InviteSummary }
  | {
      type: "set-status";
      kind: InvitePageViewKind;
      title: string | null;
      body: string | null;
      sharedItemKind?: InviteSummary["sharedItemKind"];
    }
  | { type: "accept-start" }
  | { type: "accept-success"; message: string }
  | { type: "accept-error"; message: string };

const createInitialState = (inviteId?: string, token?: string | null): InvitePageState => {
  const normalizedToken = getNormalizedInviteToken({ token: token ?? null });
  const isValidInputs = Boolean(inviteId && normalizedToken.length > 0);

  if (!isValidInputs) {
    const { title, body } = getInviteStatusCopy({ type: InviteStatusType.NotFound });

    return {
      kind: InvitePageViewKind.NotFound,
      invite: null,
      hasAccepted: false,
      isAccepting: false,
      acceptError: null,
      statusTitle: title,
      statusBody: body,
      successMessage: null,
      lastKnownSharedItemKind: undefined,
    };
  }

  return {
    kind: InvitePageViewKind.Loading,
    invite: null,
    hasAccepted: false,
    isAccepting: false,
    acceptError: null,
    statusTitle: null,
    statusBody: null,
    successMessage: null,
    lastKnownSharedItemKind: undefined,
  };
};

const reducer = (state: InvitePageState, action: InvitePageAction): InvitePageState => {
  switch (action.type) {
    case "reset":
      return action.payload;
    case "start-loading":
      return {
        ...state,
        kind: InvitePageViewKind.Loading,
        invite: null,
        hasAccepted: false,
        isAccepting: false,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
        successMessage: null,
        lastKnownSharedItemKind: undefined,
      };
    case "set-invite":
      return {
        ...state,
        kind: InvitePageViewKind.Ready,
        invite: action.invite,
        hasAccepted: false,
        isAccepting: false,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
        successMessage: null,
        lastKnownSharedItemKind: action.invite.sharedItemKind,
      };
    case "set-status":
      return {
        ...state,
        kind: action.kind,
        invite: null,
        hasAccepted: false,
        isAccepting: false,
        acceptError: null,
        statusTitle: action.title,
        statusBody: action.body,
        successMessage: null,
        lastKnownSharedItemKind: action.sharedItemKind ?? state.lastKnownSharedItemKind,
      };
    case "accept-start":
      return {
        ...state,
        isAccepting: true,
        acceptError: null,
        statusTitle: null,
        statusBody: null,
        successMessage: null,
      };
    case "accept-success":
      return {
        ...state,
        isAccepting: false,
        hasAccepted: true,
        acceptError: null,
        successMessage: action.message,
        statusTitle: null,
        statusBody: null,
      };
    case "accept-error":
      return {
        ...state,
        isAccepting: false,
        acceptError: action.message,
      };
    default:
      return state;
  }
};

interface UseInvitePageStateArgs {
  inviteId?: string;
  token: string | null;
}

export type InvitePagePublicState = Omit<InvitePageState, "lastKnownSharedItemKind">;

type UseInvitePageStateReturn = InvitePagePublicState & {
  handleAccept: () => void;
};

export const useInvitePageState = ({ inviteId, token }: UseInvitePageStateArgs) => {
  const [state, dispatch] = useReducer(reducer, createInitialState(inviteId, token));
  const normalizedToken = getNormalizedInviteToken({ token });

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    dispatch({ type: "reset", payload: createInitialState(inviteId, token) });
  }, [inviteId, token]);

  useEffect(() => {
    if (!inviteId || normalizedToken.length === 0) {
      return;
    }

    let isCancelled = false;

    dispatch({ type: "start-loading" });

    const fetchInvite = async () => {
      const result = await fetchInviteByToken(inviteId, normalizedToken);
      await sleep(MIN_LOADING_DURATION.toMillis());

      if (isCancelled || !isMountedRef.current) {
        return;
      }

      switch (result.kind) {
        case "success":
          dispatch({ type: "set-invite", invite: result.invite });
          return;
        case "expired": {
          const { title, body } = getInviteStatusCopy({
            type: InviteStatusType.Expired,
            overrides: { title: result.message },
          });
          dispatch({ type: "set-status", kind: InvitePageViewKind.Expired, title, body });
          return;
        }
        case "already-used": {
          const { title, body } = getInviteStatusCopy({
            type: InviteStatusType.AlreadyUsed,
            overrides: { title: result.message },
          });
          dispatch({ type: "set-status", kind: InvitePageViewKind.AlreadyUsed, title, body });
          return;
        }
        case "not-found": {
          const { title, body } = getInviteStatusCopy({
            type: InviteStatusType.NotFound,
            overrides: { title: result.message },
          });
          dispatch({ type: "set-status", kind: InvitePageViewKind.NotFound, title, body });
          return;
        }
        case "error": {
          const { title, body } = getInviteStatusCopy({
            type: InviteStatusType.Error,
            overrides: { title: result.message },
          });
          dispatch({ type: "set-status", kind: InvitePageViewKind.Error, title, body });
          return;
        }
        default:
          return;
      }
    };

    void fetchInvite();

    return () => {
      isCancelled = true;
    };
  }, [inviteId, normalizedToken]);

  const handleAccept = useCallback(async () => {
    if (
      !inviteId ||
      normalizedToken.length === 0 ||
      state.kind !== InvitePageViewKind.Ready ||
      state.isAccepting
    ) {
      return;
    }

    dispatch({ type: "accept-start" });

    const result = await acceptInviteByToken(inviteId, normalizedToken);

    if (!isMountedRef.current) {
      return;
    }

    const sharedItemKind = state.invite?.sharedItemKind ?? state.lastKnownSharedItemKind;

    switch (result.kind) {
      case "accepted": {
        const message = getInviteAcceptedMessage({
          invite: state.invite,
          overrideMessage: result.message,
        });
        dispatch({ type: "accept-success", message });
        return;
      }
      case "expired": {
        const { title, body } = getInviteStatusCopy({
          type: InviteStatusType.Expired,
          overrides: {
            title: result.message ?? "This invitation has expired.",
          },
        });
        dispatch({
          type: "set-status",
          kind: InvitePageViewKind.Expired,
          title,
          body,
          sharedItemKind: sharedItemKind,
        });
        return;
      }
      case "already-used": {
        const { title, body } = getInviteStatusCopy({
          type: InviteStatusType.AlreadyUsed,
          overrides: {
            title: result.message ?? "This invitation has already been used.",
          },
        });
        dispatch({
          type: "set-status",
          kind: InvitePageViewKind.AlreadyUsed,
          title,
          body,
          sharedItemKind: sharedItemKind,
        });
        return;
      }
      case "not-found": {
        const { title, body } = getInviteStatusCopy({
          type: InviteStatusType.NotFound,
          overrides: {
            title: result.message ?? "We couldn't open this invitation.",
          },
        });
        dispatch({
          type: "set-status",
          kind: InvitePageViewKind.NotFound,
          title,
          body,
          sharedItemKind: sharedItemKind,
        });
        return;
      }
      case "error": {
        dispatch({ type: "accept-error", message: result.message });
        return;
      }
      default:
        dispatch({ type: "accept-error", message: "Something went wrong. Please try again." });
    }
  }, [
    inviteId,
    normalizedToken,
    state.invite,
    state.isAccepting,
    state.lastKnownSharedItemKind,
    state.kind,
  ]);

  const { lastKnownSharedItemKind: _lastKnownSharedItemKind, ...publicState } = state;

  const result: UseInvitePageStateReturn = {
    ...publicState,
    handleAccept,
  };

  return result;
};
